import React from 'react';
import {
  HiOutlineClipboardCopy,
  HiOutlineInformationCircle,
} from 'react-icons/hi';
import { Text } from '@/components/atoms/AppText';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { COOLER_TRANSACTION_TEST_IDS as TID } from '@/constant/testIds/coolerTransaction.testIds';
import { tid } from '@/utils/testId';

export const renderErrorTemplate = (payload: any) => {
  const hasDetails = !!(
    payload?.messageDetails && payload.messageDetails.length > 0
  );
  const statusCode =
    payload?.statusCode || ORGANISM_PROPS.TOAST.DEFAULT_ERROR_STATUS;
  const isValidationError = statusCode === 422;
  const hasMessage = !!(payload?.message && String(payload.message).trim());
  const errorMessageTestId = tid(TID.SEARCH_VALIDATION_ERROR);

  const statusBadgeClass = `shrink-0 px-1.5 py-0.5 rounded-md text-[10px] font-bold border ${
    isValidationError
      ? 'bg-amber-500/10 border-amber-400/30 text-amber-600 dark:text-amber-400'
      : 'bg-red-500/10 border-red-400/30 text-red-600 dark:text-red-400'
  }`;

  return (
    <div className="flex flex-col gap-2 text-left">
      {/* Main message + status badge */}
      <div className="flex items-start justify-between gap-2">
        {hasMessage ? (
          <>
            <Text
              as="span"
              size="xl"
              weight="semibold"
              color="default"
              leading="snug"
              className="flex-1"
              data-testid={errorMessageTestId}
            >
              {payload.message}
            </Text>
            <Text
              as="span"
              size="xs"
              weight="bold"
              color="inherit"
              className={statusBadgeClass}
            >
              {statusCode}
            </Text>
          </>
        ) : (
          /* No message — show status code as the primary content */
          <Text
            as="span"
            size="xl"
            weight="bold"
            color="inherit"
            className={`${statusBadgeClass} px-2 py-1`}
          >
            {statusCode}
          </Text>
        )}
      </div>

      {/* Field-level errors */}
      {hasDetails && (
        <div className="flex flex-col gap-1.5 pl-3 border-l-2 border-red-200 dark:border-red-800/60">
          {payload.messageDetails.map((err: any, index: number) => {
            const fieldName =
              typeof err.targetField === 'object'
                ? err.targetField.missingProperty || err.targetField.field
                : err.targetField;
            return (
              <div key={index} className="flex flex-col gap-0.5">
                <Text
                  as="span"
                  size="2xs"
                  weight="bold"
                  color="subtle"
                  tracking="wide"
                  className="uppercase"
                >
                  {fieldName?.replace(/([A-Z])/g, ' $1') || 'Field Error'}
                </Text>
                <Text as="span" size="base" color="soft">
                  {err.message}
                </Text>
              </div>
            );
          })}
        </div>
      )}

      {/* Error code footer */}
      {/* {payload.errorCode && (
        <div className="flex items-center justify-between pt-1.5 mt-0.5 border-t border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center gap-1 text-[9px] font-mono text-gray-400 dark:text-gray-500">
            <HiOutlineInformationCircle className="text-[10px]" />
            {payload.errorCode}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(payload.errorCode || "");
            }}
            className="flex items-center gap-1 text-[9px] font-semibold text-blue-500 hover:text-blue-600 dark:text-blue-400 transition-colors"
          >
            <HiOutlineClipboardCopy className="text-[10px]" />
            Copy ID
          </button>
        </div>
      )} */}
    </div>
  );
};

export const renderSyncWarningTemplate = (payload: any) => {
  const { failed = [], succeeded = [] } = payload?.syncDetails || {};

  return (
    <div className="flex flex-col gap-2 text-left">
      <Text as="span" size="xl" weight="semibold" color="default" leading="snug">
        {payload.message}
      </Text>

      {succeeded.length > 0 && (
        <div className="flex flex-col gap-1">
          <Text
            as="span"
            size="2xs"
            weight="bold"
            color="subtle"
            tracking="wide"
            className="uppercase"
          >
            Synced
          </Text>
          <div className="flex flex-wrap gap-1">
            {succeeded.map((svc: string) => (
              <span
                key={svc}
                className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/10 border border-emerald-400/30 text-emerald-600 dark:text-emerald-400"
              >
                {svc}
              </span>
            ))}
          </div>
        </div>
      )}

      {failed.length > 0 && (
        <div className="flex flex-col gap-1.5 pl-3 border-l-2 border-amber-300 dark:border-amber-700/60">
          <Text
            as="span"
            size="2xs"
            weight="bold"
            color="subtle"
            tracking="wide"
            className="uppercase"
          >
            Sync Failed
          </Text>
          {failed.map((item: { service: string; message: string; statusCode?: number }) => (
            <div key={item.service} className="flex items-start justify-between gap-2">
              <div className="flex flex-col gap-0.5">
                <span className="self-start px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/15 border border-amber-400/40 text-amber-700 dark:text-amber-300">
                  {item.service}
                </span>
                <Text as="span" size="base" color="soft">
                  {item.message}
                </Text>
              </div>
              {item.statusCode && (
                <Text
                  as="span"
                  size="xs"
                  weight="bold"
                  color="inherit"
                  className="shrink-0 px-1.5 py-0.5 rounded-md text-[10px] font-bold border bg-amber-500/10 border-amber-400/30 text-amber-600 dark:text-amber-400"
                >
                  {item.statusCode}
                </Text>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
